/** Where the user left the support button: a screen side and a distance from the bottom, in px. */
export interface SupportLauncherPosition {
  side: 'left' | 'right';
  bottom: number;
}

export const SUPPORT_LAUNCHER_STORAGE_KEY = 'vult.support.launcher';
/** Pointer travel (px) before a press becomes a drag instead of a click. */
export const SUPPORT_DRAG_THRESHOLD = 6;

/**
 * Snaps a dropped button to the nearer side and keeps it fully on screen.
 * `x`/`y` are the pointer position, `size` the button size, `gap` the edge margin.
 */
export function snapLauncher(x: number, y: number, viewport: { width: number; height: number }, size: number, gap: number): SupportLauncherPosition {
  const side = x < viewport.width / 2 ? 'left' : 'right';
  const bottom = viewport.height - y - size / 2;
  return { side, bottom: Math.round(clamp(bottom, gap, viewport.height - size - gap)) };
}

/** A button in the upper half opens its panel below it; otherwise the panel opens above. */
export function opensBelow(position: SupportLauncherPosition, viewportHeight: number, size: number): boolean {
  return position.bottom + size / 2 > viewportHeight / 2;
}

export function loadLauncherPosition(storage: Storage | null): SupportLauncherPosition | null {
  try {
    const parsed = JSON.parse(storage?.getItem(SUPPORT_LAUNCHER_STORAGE_KEY) || 'null');
    const valid = parsed && (parsed.side === 'left' || parsed.side === 'right') && Number.isFinite(parsed.bottom) && parsed.bottom >= 0;
    return valid ? { side: parsed.side, bottom: Number(parsed.bottom) } : null;
  } catch {
    return null;
  }
}

export function saveLauncherPosition(storage: Storage | null, position: SupportLauncherPosition): void {
  try {
    storage?.setItem(SUPPORT_LAUNCHER_STORAGE_KEY, JSON.stringify(position));
  } catch {
    // Private mode or a full storage: the position simply is not remembered.
  }
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), Math.max(min, max));
}
